/* Phase 10 — Hospital admin Branding page. Two-column form + live preview.
 * Left: master toggle, mode, 8 color pickers, logo/favicon/brand name, save/reset.
 * Right: mini patient-facing preview that updates as fields change.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save, Palette, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { api, apiError } from "../../lib/api";
import { PLATFORM_DEFAULT_THEME, useTheme } from "../../context/ThemeProvider";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const COLOR_FIELDS = [
    ["primary_color", "Primary"],
    ["accent_color", "Accent"],
    ["secondary_color", "Secondary"],
    ["background_color", "Background"],
    ["surface_color", "Surface"],
    ["border_color", "Border"],
    ["text_primary_color", "Text primary"],
    ["text_secondary_color", "Text secondary"],
];

export default function Branding() {
    const { applyTheme, activeTheme, loadOwnHospitalTheme } = useTheme();
    const [saved, setSaved] = useState(null);    // last-saved snapshot
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [err, setErr] = useState("");

    // load hospital theme + apply
    useEffect(() => {
        setBusy(true);
        api.get("/hospital/theme")
            .then((r) => { setSaved(r.data); setDraft(r.data); applyTheme(r.data); })
            .catch((e) => setErr(apiError(e, "Failed to load theme")))
            .finally(() => setBusy(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dirty = useMemo(() => JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);

    const update = (k, v) => setDraft((d) => ({ ...(d || {}), [k]: v }));

    // whenever the draft changes, update the live theme (best-effort — colors must be valid)
    useEffect(() => {
        if (!draft) return;
        const clean = { ...draft };
        for (const [k] of COLOR_FIELDS) {
            if (!HEX_RE.test(clean[k] || "")) clean[k] = saved?.[k] || PLATFORM_DEFAULT_THEME[k];
        }
        applyTheme(clean);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft]);

    const validate = () => {
        for (const [k, label] of COLOR_FIELDS) {
            if (!HEX_RE.test(draft?.[k] || "")) return `${label} must be a 6-digit hex like #14b8a6`;
        }
        return null;
    };

    const onSave = async () => {
        const invalid = validate();
        if (invalid) { setErr(invalid); return; }
        setSaving(true); setErr("");
        try {
            const payload = {
                mode: draft.mode,
                primary_color: draft.primary_color,
                secondary_color: draft.secondary_color,
                accent_color: draft.accent_color,
                background_color: draft.background_color,
                surface_color: draft.surface_color,
                border_color: draft.border_color,
                text_primary_color: draft.text_primary_color,
                text_secondary_color: draft.text_secondary_color,
                logo_url: draft.logo_url || null,
                favicon_url: draft.favicon_url || null,
                brand_name: draft.brand_name || null,
                is_active: !!draft.is_active,
            };
            const { data } = await api.patch("/hospital/theme", payload);
            setSaved(data); setDraft(data); applyTheme(data);
            toast.success("Branding saved");
        } catch (e) {
            setErr(apiError(e, "Save failed"));
        } finally { setSaving(false); }
    };

    const onReset = async () => {
        setSaving(true); setErr("");
        try {
            const { data } = await api.post("/hospital/theme/reset");
            setSaved(data); setDraft(data); applyTheme(data);
            toast.success("Reset to platform defaults");
            setResetOpen(false);
            loadOwnHospitalTheme();
        } catch (e) {
            setErr(apiError(e, "Reset failed"));
        } finally { setSaving(false); }
    };

    if (busy || !draft) {
        return <div className="text-sm text-mb-fg2 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading branding…</div>;
    }

    return (
        <div className="space-y-6" data-testid="branding-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400 inline-flex items-center gap-2">
                        <Palette className="h-3 w-3" /> Branding
                    </div>
                    <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight font-light">Custom theme</h1>
                    <div className="mt-1 text-sm text-mb-fg2">
                        Change how patients see your hospital across MediBooks.
                        Colors apply everywhere — sidebar, buttons, charts, patient booking pages.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setResetOpen(true)} data-testid="reset-defaults-btn"
                        className="bg-transparent border-mb-border hover:bg-mb-surface2 text-mb-fg">
                        <RotateCcw className="h-4 w-4 mr-2" /> Reset defaults
                    </Button>
                    <Button onClick={onSave} disabled={!dirty || saving} data-testid="save-theme-btn"
                        className="bg-teal-500 hover:bg-teal-400 text-zinc-950 disabled:opacity-40">
                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save changes
                    </Button>
                </div>
            </div>

            {err && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="branding-error">
                    {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* -------- form -------- */}
                <div className="rounded-xl border border-mb-border bg-mb-surface p-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-sm text-mb-fg">Enable custom theme</Label>
                            <div className="text-xs text-mb-fg2 mt-1">
                                When off, patients see MediBooks' default palette.
                            </div>
                        </div>
                        <Switch checked={!!draft.is_active} onCheckedChange={(v) => update("is_active", v)}
                            data-testid="branding-active-switch" />
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Mode</Label>
                        <Select value={draft.mode} onValueChange={(v) => update("mode", v)}>
                            <SelectTrigger data-testid="branding-mode" className="mt-2 bg-mb-surface2 border-mb-border text-mb-fg h-11">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-mb-surface2 border-mb-border text-mb-fg">
                                <SelectItem value="dark">Dark</SelectItem>
                                <SelectItem value="light">Light</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Colors</Label>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {COLOR_FIELDS.map(([k, label]) => (
                                <ColorInput key={k} label={label} value={draft[k]}
                                    onChange={(v) => update(k, v)} testid={`branding-${k}`} />
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Logo URL</Label>
                            <Input value={draft.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)}
                                placeholder="https://…" data-testid="branding-logo-url"
                                className="mt-2 bg-mb-surface2 border-mb-border text-mb-fg h-11" />
                            {draft.logo_url && (
                                <img src={draft.logo_url} alt="" className="mt-2 h-8 w-auto rounded bg-mb-surface2 border border-mb-border p-1" />
                            )}
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Favicon URL</Label>
                            <Input value={draft.favicon_url || ""} onChange={(e) => update("favicon_url", e.target.value)}
                                placeholder="https://…/favicon.ico" data-testid="branding-favicon-url"
                                className="mt-2 bg-mb-surface2 border-mb-border text-mb-fg h-11" />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-mb-fg2">Brand name override</Label>
                        <Input value={draft.brand_name || ""} onChange={(e) => update("brand_name", e.target.value)}
                            placeholder="Leave blank to use hospital name" data-testid="branding-brand-name"
                            className="mt-2 bg-mb-surface2 border-mb-border text-mb-fg h-11" />
                    </div>
                </div>

                {/* -------- live preview -------- */}
                <div className="lg:sticky lg:top-6 self-start">
                    <div className="text-xs uppercase tracking-[0.2em] text-mb-fg2 mb-3 inline-flex items-center gap-2">
                        <Sparkles className="h-3 w-3 text-teal-400" /> Live preview
                    </div>
                    <PreviewCard theme={draft} />
                    <div className="mt-3 text-[11px] text-mb-fg2">
                        Preview reflects unsaved changes. Save to publish.
                    </div>
                </div>
            </div>

            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                <DialogContent className="bg-mb-surface border-mb-border text-mb-fg max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-light text-2xl">Reset to platform defaults?</DialogTitle>
                        <DialogDescription className="text-mb-fg2 text-sm">
                            Custom colors, logo and brand name will be cleared. Patients will see MediBooks' default look.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setResetOpen(false)} className="hover:bg-mb-surface2">Cancel</Button>
                        <Button onClick={onReset} disabled={saving} data-testid="confirm-reset-btn"
                            className="bg-red-500 hover:bg-red-400 text-white">
                            {saving ? "Resetting…" : "Yes, reset"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* no-op read to silence unused warning if activeTheme not shown */}
            <span className="hidden">{activeTheme?.mode}</span>
        </div>
    );
}

function ColorInput({ label, value, onChange, testid }) {
    const valid = HEX_RE.test(value || "");
    return (
        <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-mb-fg2 mb-1.5">{label}</div>
            <div className="flex items-center gap-2">
                <input type="color" value={valid ? value : "#000000"} onChange={(e) => onChange(e.target.value)}
                    data-testid={`${testid}-picker`}
                    className="h-11 w-11 rounded-md border border-mb-border bg-mb-surface2 cursor-pointer" />
                <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
                    data-testid={`${testid}-hex`}
                    className={`bg-mb-surface2 border-mb-border text-mb-fg h-11 font-mono text-sm ${valid ? "" : "border-red-500/50"}`} />
            </div>
        </div>
    );
}

function PreviewCard({ theme }) {
    // Render preview using inline styles (not Tailwind) so it doesn't fight the
    // real page while dirty state hasn't been "applied" globally.
    const t = { ...PLATFORM_DEFAULT_THEME, ...(theme || {}) };
    const s = {
        bg: t.background_color,
        surf: t.surface_color,
        border: t.border_color,
        primary: t.primary_color,
        accent: t.accent_color,
        fg: t.text_primary_color,
        fg2: t.text_secondary_color,
    };
    return (
        <div style={{
            background: s.bg, color: s.fg, borderColor: s.border,
        }}
            className="rounded-xl border p-5 space-y-4" data-testid="branding-preview">
            <div className="flex items-center gap-3">
                {t.logo_url ? (
                    <img src={t.logo_url} alt="" className="h-8 w-8 rounded object-contain bg-white/5 p-1" />
                ) : (
                    <div style={{ background: s.primary, color: s.bg }}
                        className="h-8 w-8 rounded flex items-center justify-center font-bold text-sm">
                        {(t.brand_name || "MB").slice(0, 1).toUpperCase()}
                    </div>
                )}
                <div>
                    <div style={{ color: s.fg }} className="font-semibold">{t.brand_name || "Your hospital"}</div>
                    <div style={{ color: s.fg2 }} className="text-[10px] uppercase tracking-[0.25em]">patient booking</div>
                </div>
            </div>
            <div style={{ background: s.surf, borderColor: s.border }} className="rounded-lg border p-4 space-y-2">
                <div className="flex items-center gap-3">
                    <div style={{ background: `${s.primary}20`, color: s.accent }}
                        className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold">
                        DJ
                    </div>
                    <div className="flex-1 min-w-0">
                        <div style={{ color: s.fg }} className="text-sm">Dr. Jane Doe</div>
                        <div style={{ color: s.fg2 }} className="text-xs">Cardiology · ₹120</div>
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between">
                <div style={{ color: s.fg2 }} className="text-xs">
                    <div><span style={{ color: s.accent }} className="font-medium">◖</span> 3 slots today</div>
                </div>
                <button style={{ background: s.primary, color: s.bg }}
                    className="px-4 py-2 rounded-lg text-sm font-medium">
                    Book appointment
                </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
                {[
                    ["9", "doctors"],
                    ["₹4.2k", "today"],
                    ["24", "bookings"],
                ].map(([v, l]) => (
                    <div key={l} style={{ background: s.surf, borderColor: s.border }}
                        className="rounded-md border p-2">
                        <div style={{ color: s.fg }} className="text-sm font-semibold">{v}</div>
                        <div style={{ color: s.fg2 }} className="text-[10px] uppercase tracking-[0.15em]">{l}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
